// features/auth/AuthScene.tsx — the sign-in / sign-up page.
//
// One ruled page, not a card on a backdrop. A centred 1120px frame holds the
// public record on the left (RecordHead over RecordBody) and the form column on
// the right, both typeset onto a single bgRoot ground whose 24px ruling shows
// in the margins and the gutter and never under text (see AuthRecord's
// textBlock). No card, no sheet, no vertical centring: the wordmark and the
// record's title share a line box at the top, and the page scrolls if the form
// outgrows the viewport.
//
// DOM order is head → form → rest, so tab order reaches the form before the
// record's /council link, at every width. Below 1024px the three stack in that
// order as bands with hairlines between them; the record is content, present at
// every width — it is not "art" to be hidden.

import { useRef } from 'react';
import styled from 'styled-components';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useSession } from '@/hooks';
import { query } from '@/theme/breakpoints';
import { deriveRegister } from '@/theme/presets/deepSignal';
import { AuthCard } from './AuthCard';
import { RecordBody, RecordHead, textBlock } from './AuthRecord';
import { safeNext } from './safeNext';
import { useAuthEntrance } from './useAuthEntrance';

const Page = styled.div`
  position: relative;
  min-height: 100vh;
  box-sizing: border-box;
  padding: 72px 48px 24px;
  background: ${({ theme }) => theme.color.bgRoot};
  @media ${query.tabletDown} {
    padding: 0;
  }
`;

/* The Register ruling at the shell backdrop's own rule alpha — NOT glassBorder,
   whose 0.085 / 0.13 is what made the old panel read as notebook paper. */
const Ruling = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: repeating-linear-gradient(
    0deg,
    transparent 0 23px,
    ${({ theme }) => deriveRegister(theme.color.accentCyan, theme.mode).rule} 23px 24px
  );
  @media ${query.tabletDown} {
    display: none;
  }
`;

const Frame = styled.div`
  position: relative;
  width: min(1120px, 100%);
  margin-inline: auto;
  min-height: calc(100vh - 96px);
  display: grid;
  grid-template-columns: minmax(360px, 528px) minmax(72px, 1fr) 400px;
  grid-template-rows: auto 1fr;
  grid-template-areas:
    'head . form'
    'body . form';
  align-items: start;
  @media ${query.tabletDown} {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 100dvh;
  }
`;

const FormColumn = styled.div`
  grid-area: form;
  align-self: start;
  display: flex;
  flex-direction: column;
  ${textBlock}
  @media ${query.tabletDown} {
    width: 100%;
    max-width: 480px;
    padding: 32px 40px 40px;
  }
  @media ${query.mobile} {
    max-width: none;
    padding: 24px 20px 32px;
  }
`;

export function AuthScene({ mode }: { mode: 'signin' | 'signup' }) {
  const { isAuthenticated } = useSession();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));

  // Keep hooks unconditional — before the early return.
  const cardRef = useRef<HTMLDivElement>(null);
  useAuthEntrance(cardRef);

  if (isAuthenticated) return <Navigate to={next ?? '/'} replace />;

  return (
    <Page>
      <Ruling aria-hidden />
      <Frame>
        <RecordHead />
        <FormColumn ref={cardRef}>
          <AuthCard mode={mode} next={next} />
        </FormColumn>
        <RecordBody />
      </Frame>
    </Page>
  );
}
